import React, { useState, useEffect, useMemo } from "react";
import "./Quiz.css";
import { useNavigate } from "react-router-dom";

function PersonalityQuiz() {
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  // Fetch personality quiz questions
  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/quiz?type=personality`,
      {
        method: "GET",
        credentials: "include",
      }
    )
      .then((response) => response.json())
      .then((data) => {
        setQuizQuestions(data[0]);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  // Likert scale
  const answerOptions = [
    "Very Inaccurate",
    "Moderately Inaccurate",
    "Neither Accurate Nor Inaccurate",
    "Moderately Accurate",
    "Very Accurate",
  ];

  const scores = {
    "Very Inaccurate": 1,
    "Moderately Inaccurate": 2,
    "Neither Accurate Nor Inaccurate": 3,
    "Moderately Accurate": 4,
    "Very Accurate": 5,
  };

  // Create quiz data
  const quizData = useMemo(() => {
    return quizQuestions.map((q) => ({
      question: q[0],
      scoreType: q[1],
      category: q[2],
    }));
  }, [quizQuestions]);

  const questionsPerPage = 22;
  const totalPages = Math.ceil(quizData.length / questionsPerPage);

  const currentQuestions = quizData.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const handleAnswerChange = (index, option) => {
    setQuizAnswers({
      ...quizAnswers,
      [index]: option,
    });
  };

  const allQuestionsAnswered = currentQuestions.every(
    (_, index) =>
      quizAnswers[currentPage * questionsPerPage + index] !== undefined
  );

  // Normalize negative vs positive scoring
  const getNormalisedScore = (scoreType, answerScore) => {
    return scoreType === "negative"
      ? Object.keys(scores).length - answerScore + 1
      : answerScore;
  };

  // Personality scoring logic
  const calculateScore = () => {
    const categories = [
      "Extraversion",
      "Agreeableness",
      "Conscientiousness",
      "Emotional Stability",
      "Intellect/Imagination",
    ];
    const scoreDict = {};
    const maxDict = {};

    categories.forEach((cat) => {
      scoreDict[cat] = 0;
      maxDict[cat] = 0;
    });

    Object.keys(quizAnswers).forEach((qIndex) => {
      const answer = quizAnswers[qIndex];
      const { scoreType, category } = quizData[qIndex];
      const answerScore = scores[answer];
      const normalisedScore = getNormalisedScore(scoreType, answerScore);

      if (categories.includes(category)) {
        scoreDict[category] += normalisedScore;
        maxDict[category] += 5; // max per question = 5
      }
    });

    return { scoreDict, maxDict };
  };

  const handleSubmit = async () => {
    try {
      const { scoreDict, maxDict } = calculateScore();

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_FLASK_API_URL}/score/personality`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            score: Object.entries(scoreDict).reduce(
              (acc, [trait, score]) => ({
                ...acc,
                [trait]: [score, maxDict[trait]],
              }),
              {}
            ),
            quizType: "personality",
          }),
        }
      );

      const data = await response.json();
      if (response.ok) {
        console.log("Personality score stored:", data);
        navigate(`/results-personality`);
        console.error("Error storing personality score:", data.error);
      }
    } catch (error) {
      console.error("Error submitting personality quiz score:", error);
    }
  };

  // handling the logout button
  const handleLogout = () => {
    fetch(`${process.env.REACT_APP_BACKEND_FLASK_API_URL}/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data.message);
        navigate(`/`);
      })
      .catch((error) => console.error("Error logging out:", error));
  };

  return (
    <div className="quiz-container">
      <header className="dash-header">
        <button onClick={() => navigate(`/dashboard`)}>Dashboard</button>
        <button onClick={handleLogout}>Log Out</button>
      </header>

      <h2 style={{ textAlign: "center" }}>Personality Quiz</h2>
      <h4 style={{ textAlign: "center" }}>
        All questions need to be answered.
      </h4>

      {currentQuestions.map((q, index) => {
        const globalIndex = currentPage * questionsPerPage + index;
        return (
          <div key={globalIndex} className="question-container">
            <h3>
              {globalIndex + 1}. {q.question}
            </h3>
            <div className="likert-scale">
              {answerOptions.map((option, optionIndex) => (
                <button
                  key={optionIndex}
                  className={
                    quizAnswers[globalIndex] === option ? "selected" : ""
                  }
                  onClick={() => handleAnswerChange(globalIndex, option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {currentPage >= 1 && (
        <button
          className="back-btn"
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Back
        </button>
      )}

      {currentPage === totalPages - 1 ? (
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={!allQuestionsAnswered}
        >
          Submit
        </button>
      ) : (
        <button
          className="submit-btn"
          onClick={() => {
            setCurrentPage(currentPage + 1);
            window.scrollTo(0, 0); // Scroll back to the top of the page
          }}
          disabled={!allQuestionsAnswered}
        >
          Next
        </button>
      )}
    </div>
  );
}

export default PersonalityQuiz;
